import React, { useMemo, useState } from "react";
import api from "../../../../api";
import RubricTreeNode, { removeFromForest } from "./RubricTreeNode";
import { describeApiError } from "./apiError";

const LessonsTab = ({
  lessons,
  loading,
  error,
  onRefresh,
  onLessonDeleted,
}) => {
  const [search, setSearch] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [lessonTrees, setLessonTrees] = useState({}); // { [lessonId]: { lessonName, rubrics } }
  const [detailLoading, setDetailLoading] = useState({});
  const [detailError, setDetailError] = useState({});
  const [deletingLessonIds, setDeletingLessonIds] = useState({});
  const [deletingRubricIds, setDeletingRubricIds] = useState({});

  const filteredLessons = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return lessons;
    return lessons.filter((l) => l.name?.toLowerCase().includes(q));
  }, [lessons, search]);

  const fetchLessonTree = async (id) => {
    setDetailLoading((prev) => ({ ...prev, [id]: true }));
    setDetailError((prev) => ({ ...prev, [id]: "" }));
    try {
      const res = await api.get(`/api/repertory/lessons/${id}`);
      setLessonTrees((prev) => ({ ...prev, [id]: res.data }));
    } catch (err) {
      console.error(err);
      setDetailError((prev) => ({
        ...prev,
        [id]: "Failed to load this chapter's rubrics.",
      }));
    } finally {
      setDetailLoading((prev) => ({ ...prev, [id]: false }));
    }
  };

  const toggleExpand = (lesson) => {
    if (expandedId === lesson.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(lesson.id);
    if (!lessonTrees[lesson.id]) {
      fetchLessonTree(lesson.id);
    }
  };

  const handleDeleteLesson = async (id) => {
    if (
      !window.confirm(
        "Delete this chapter/lesson? All rubrics under it will be removed too. This cannot be undone."
      )
    ) {
      return;
    }

    setDeletingLessonIds((prev) => ({ ...prev, [id]: true }));
    try {
      await api.delete(`/api/repertory/lessons/${id}`);
      onLessonDeleted(id);
      setLessonTrees((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      if (String(expandedId) === String(id)) setExpandedId(null);
    } catch (err) {
      console.error(err);
      alert(describeApiError(err, "Failed to delete this chapter."));
    } finally {
      setDeletingLessonIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  const handleDeleteRubric = async (id) => {
    if (
      !window.confirm(
        "Delete this rubric? This will also remove any sub-rubrics under it and cannot be undone."
      )
    ) {
      return;
    }

    setDeletingRubricIds((prev) => ({ ...prev, [id]: true }));
    try {
      await api.delete(`/api/repertory/rubric/${id}`);
      setLessonTrees((prev) => {
        const next = {};
        Object.keys(prev).forEach((lessonId) => {
          next[lessonId] = {
            ...prev[lessonId],
            rubrics: removeFromForest(prev[lessonId].rubrics, id),
          };
        });
        return next;
      });
    } catch (err) {
      console.error(err);
      alert(describeApiError(err, "Failed to delete rubric."));
    } finally {
      setDeletingRubricIds((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }
  };

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          <h5 className="card-title mb-0 me-auto">
            Lessons &amp; Chapters
            <span className="badge text-bg-secondary ms-2">
              {lessons.length}
            </span>
          </h5>
          <input
            type="search"
            className="form-control"
            style={{ maxWidth: "280px" }}
            placeholder="Search by chapter name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={onRefresh}
            disabled={loading}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </button>
        </div>

        {loading && (
          <div className="text-center py-4">
            <span className="spinner-border" role="status" />
          </div>
        )}

        {!loading && error && (
          <div className="alert alert-danger d-flex justify-content-between align-items-center">
            <span>{error}</span>
            <button className="btn btn-sm btn-outline-danger" onClick={onRefresh}>
              Retry
            </button>
          </div>
        )}

        {!loading && !error && lessons.length === 0 && (
          <p className="text-muted mb-0">
            No chapters/lessons have been uploaded yet — use the{" "}
            <strong>Upload JSON</strong> tab to add one.
          </p>
        )}

        {!loading && !error && lessons.length > 0 && filteredLessons.length === 0 && (
          <p className="text-muted mb-0">
            No chapters match &ldquo;{search}&rdquo;.
          </p>
        )}

        {!loading && filteredLessons.length > 0 && (
          <div className="list-group">
            {filteredLessons.map((lesson) => (
              <div key={lesson.id} className="list-group-item">
                <div className="d-flex align-items-center gap-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-decoration-none p-0"
                    onClick={() => toggleExpand(lesson)}
                  >
                    {expandedId === lesson.id ? "▼" : "▶"}
                  </button>
                  <span
                    className="fw-semibold flex-grow-1"
                    role="button"
                    onClick={() => toggleExpand(lesson)}
                    style={{ cursor: "pointer" }}
                  >
                    {lesson.name}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => toggleExpand(lesson)}
                  >
                    {expandedId === lesson.id ? "Hide" : "View"}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => handleDeleteLesson(lesson.id)}
                    disabled={!!deletingLessonIds[lesson.id]}
                  >
                    {deletingLessonIds[lesson.id] ? "Deleting…" : "Delete"}
                  </button>
                </div>

                {expandedId === lesson.id && (
                  <div className="mt-3 ps-4 border-start">
                    {detailLoading[lesson.id] && (
                      <div className="text-muted small">
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        />
                        Loading rubrics…
                      </div>
                    )}
                    {detailError[lesson.id] && (
                      <div className="text-danger small">
                        {detailError[lesson.id]}{" "}
                        <button
                          className="btn btn-sm btn-link p-0"
                          onClick={() => fetchLessonTree(lesson.id)}
                        >
                          Retry
                        </button>
                      </div>
                    )}
                    {lessonTrees[lesson.id]?.rubrics?.length === 0 && (
                      <p className="text-muted small mb-0">
                        This chapter has no rubrics yet.
                      </p>
                    )}
                    {lessonTrees[lesson.id]?.rubrics?.map((rubric) => (
                      <RubricTreeNode
                        key={rubric.id}
                        node={rubric}
                        depth={0}
                        deletingIds={deletingRubricIds}
                        onDelete={handleDeleteRubric}
                      />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LessonsTab;
